import React, { useState } from "react";
import { Clickable, Icon, LoadingIndicator, Text } from "@gusarov-studio/rubik-ui";

export type ToolCallStatus = "running" | "done" | "error";

interface ToolCallProps {
  children: React.ReactNode;
  name: string;
  status: ToolCallStatus;
  durationSeconds?: number;
  defaultOpen?: boolean;
}

/** Tool calls are often sub-second, so keep one decimal until they aren't. */
function formatDuration(seconds: number): string {
  return seconds < 10 ? seconds.toFixed(1) : String(Math.round(seconds));
}

function getToolCallLabel(name: string, status: ToolCallStatus, durationSeconds?: number): string {
  if (status === "running") {
    return `Calling ${name}...`;
  }
  const label = status === "error" ? `${name} failed` : `Called ${name}`;
  if (durationSeconds != null) {
    return `${label} in ${formatDuration(durationSeconds)}s`;
  }
  return label;
}

/**
 * Collapsible block for one tool call, mirroring `MessageReasoning` so a run's
 * output reads the same whether the model was thinking or calling a tool.
 */
const ToolCall: React.FC<ToolCallProps> = ({
  children,
  name,
  status,
  durationSeconds,
  defaultOpen = false,
}) => {
  "use memo";
  const [open, setOpen] = useState(defaultOpen || status === "running");
  const label = getToolCallLabel(name, status, durationSeconds);

  return (
    <div>
      {status === "running" && (
        <div className="flex items-center gap-1 py-[5px]">
          <LoadingIndicator
            size={16}
            type="one-eighty-ring-withBg"
            variant="primary"
          />
          <Text
            size="12"
            color="muted"
            weight="500"
          >
            {label}
          </Text>
        </div>
      )}

      {status !== "running" && (
        <Clickable onClick={() => setOpen((prev) => !prev)}>
          <div className="flex items-center gap-1 py-[5px]">
            <Text
              size="12"
              color={status === "error" ? "danger" : "muted"}
              weight="500"
            >
              {label}
            </Text>
            <Icon
              glyph={open ? "chevron-down" : "chevron-right"}
              color="muted"
              size={14}
              strokeWidth={2}
            />
          </div>
        </Clickable>
      )}

      {open && (
        <div className="border-l-3 border-secondary mb-4 ml-1.5 mt-1 flex flex-col gap-2 pl-3">
          {children}
        </div>
      )}
    </div>
  );
};

export { ToolCall };
